/**
 * Bulk Add Locations Modal
 * Smart multi-facility location addition with conflict detection
 *
 * UX Pattern: Progressive Disclosure + Preview (Notion, Linear, Airtable)
 *
 * Features:
 * - Multi-step flow (Input → Preview → Confirm)
 * - Conflict detection (existing locations)
 * - Merge strategy with smart defaults
 * - Per-facility preview
 * - Skip duplicates automatically
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Plus,
  Check,
  AlertTriangle,
  CheckCircle2,
  Users,
  ChevronRight,
  Loader2,
  Info,
} from 'lucide-react';
import { getLocationsByFacility, createLocationInMultipleFacilities } from '../../services/locationsService';
import { useFeedbackContext } from '../../context';
import { serviceLogger } from '../../utils/serviceLogger';

export const BulkAddLocationsModal = ({
  isOpen,
  onClose,
  facilities = [],
  selectedFacilityIds = [],
  organizationId,
  onSuccess,
}) => {
  const { showToast } = useFeedbackContext();

  const [step, setStep] = useState('input'); // 'input' | 'preview' | 'processing'
  const [locationInput, setLocationInput] = useState('');
  const [locationNames, setLocationNames] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [loadedFacilities, setLoadedFacilities] = useState(facilities);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);

  // Update loadedFacilities when facilities prop changes (with names)
  useEffect(() => {
    if (facilities.length > 0 && facilities.every(f => f.name && f.id)) {
      // Only update if facilities match selectedFacilityIds
      const matchingFacilities = facilities.filter(f => selectedFacilityIds.includes(f.id));
      if (matchingFacilities.length === selectedFacilityIds.length) {
        setLoadedFacilities(matchingFacilities);
      }
    }
  }, [facilities, selectedFacilityIds]);

  // Use loadedFacilities count if available, otherwise use selectedFacilityIds length
  const facilitiesCount = loadedFacilities.length > 0 
    ? loadedFacilities.length 
    : (facilities.length > 0 ? facilities.length : selectedFacilityIds.length);

  // Load ALL selected facilities when modal opens (source of truth: selectedFacilityIds)
  useEffect(() => {
    const loadFacilities = async () => {
      if (!isOpen || selectedFacilityIds.length === 0) {
        if (facilities.length > 0) setLoadedFacilities(facilities);
        return;
      }

      // Use parent-provided facilities if they match all selected IDs
      if (
        facilities.length === selectedFacilityIds.length &&
        facilities.every((f) => selectedFacilityIds.includes(f.id) && f.name)
      ) {
        setLoadedFacilities(facilities);
        setIsLoadingFacilities(false);
        return;
      }

      setIsLoadingFacilities(true);
      try {
        // Prefer getFacilitiesByOrganization so we get ALL org facilities (no pagination gap)
        if (organizationId) {
          const { getFacilitiesByOrganization } = await import('../../services/facilitiesService');
          const allFacilities = await getFacilitiesByOrganization(organizationId);
          const list = Array.isArray(allFacilities) ? allFacilities : (allFacilities?.data ?? allFacilities?.results ?? []);
          const selected = list.filter((f) => selectedFacilityIds.includes(f.id));
          if (selected.length > 0) {
            setLoadedFacilities(selected);
            setIsLoadingFacilities(false);
            return;
          }
        }

        // Fallback: getFacilities (paginated)
        const { getFacilities } = await import('../../services/facilitiesService');
        const response = await getFacilities({ page: 1, limit: 500 });
        const raw = response?.data ?? response;
        const list = Array.isArray(raw) ? raw : (raw?.data ?? raw?.results ?? []);
        const selected = list.filter((f) => selectedFacilityIds.includes(f.id));
        if (selected.length > 0) setLoadedFacilities(selected);
      } catch (error) {
        serviceLogger.error('Error loading facilities for bulk add:', error);
        setLoadedFacilities([]);
      } finally {
        setIsLoadingFacilities(false);
      }
    };

    loadFacilities();
  }, [isOpen, selectedFacilityIds, organizationId, facilities]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('input');
      setLocationInput('');
      setLocationNames([]);
      setPreviews([]);
      setSkipDuplicates(true);
      setProcessingProgress(0);
    }
  }, [isOpen]);

  // Handle adding location name
  const handleAddLocationName = () => {
    const trimmed = locationInput.trim();
    if (!trimmed) return;

    if (locationNames.includes(trimmed)) {
      showToast({ variant: 'error', title: 'Error', copy: 'Area already in list' });
      return;
    }

    setLocationNames([...locationNames, trimmed]);
    setLocationInput('');
  };

  const handleRemoveLocationName = (name) => {
    setLocationNames(locationNames.filter((n) => n !== name));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLocationName();
    }
  };

  // Build facility list from selectedFacilityIds (source of truth) so we never miss a facility
  const getFacilitiesToUse = useCallback(() => {
    const byId = new Map(
      [...loadedFacilities, ...facilities].filter((f) => f.id).map((f) => [f.id, f])
    );
    return selectedFacilityIds.map((id) => byId.get(id) || { id, name: `Facility ${String(id ?? '').slice(0, 8)}…` });
  }, [selectedFacilityIds, loadedFacilities, facilities]);

  // Generate preview: one preview per selected facility (always use selectedFacilityIds length)
  const handleGeneratePreview = async () => {
    if (locationNames.length === 0) {
      showToast({ variant: 'error', title: 'Error', copy: 'Add at least one area' });
      return;
    }

    const facilitiesToUse = getFacilitiesToUse();
    if (facilitiesToUse.length === 0) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: 'No facilities selected. Please select facilities and try again.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const previewData = [];

      for (const facility of facilitiesToUse) {
        const response = await getLocationsByFacility(facility.id);
        const existingLocations = response?.data ?? response ?? [];
        const existingNames = (Array.isArray(existingLocations) ? existingLocations : []).map(
          (loc) => (loc.name || '').toLowerCase()
        );

        const duplicates = [];
        const newLocations = [];

        locationNames.forEach((name) => {
          if (existingNames.includes(name.toLowerCase())) {
            duplicates.push(name);
          } else {
            newLocations.push(name);
          }
        });

        previewData.push({
          facility: { id: facility.id, name: facility.name || `Facility ${String(facility.id ?? '').slice(0, 8)}…` },
          existingLocations: Array.isArray(existingLocations) ? existingLocations : [],
          newLocations: skipDuplicates ? newLocations : [...locationNames],
          duplicates,
          willAdd: skipDuplicates ? newLocations.length : locationNames.length,
        });
      }

      setPreviews(previewData);
      setStep('preview');
    } catch (error) {
      showToast({ variant: 'error', title: 'Error', copy: 'Failed to generate preview' });
      serviceLogger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Execute bulk add (optimized for performance)
  const handleConfirmBulkAdd = async () => {
    setStep('processing');
    setProcessingProgress(0);

    try {
      let completed = 0;
      const total = previews.reduce((sum, p) => sum + p.willAdd, 0);

      // Process each location name across all facilities (optimized batch approach)
      for (const locationName of locationNames) {
        // Get all facility IDs that should receive this location
        const facilityIdsForThisLocation = previews
          .filter((preview) => {
            const locationsToAdd = skipDuplicates ? preview.newLocations : locationNames;
            return locationsToAdd.includes(locationName);
          })
          .map((preview) => preview.facility.id);

        if (facilityIdsForThisLocation.length > 0) {
          // Use optimized batch creation for this location name across multiple facilities
          const results = await createLocationInMultipleFacilities(
            facilityIdsForThisLocation,
            { name: locationName },
            3, // concurrency limit for performance
          );

          completed += results.length;
          setProcessingProgress(Math.round((completed / total) * 100));
        }
      }

      const facilitiesCount = loadedFacilities.length > 0 ? loadedFacilities.length : selectedFacilityIds.length;
      showToast({
        variant: 'success',
        title: 'Success',
        copy: `Areas added successfully! Added ${completed} area${
          completed > 1 ? 's' : ''
        } to ${facilitiesCount} facilit${facilitiesCount > 1 ? 'ies' : 'y'}`,
        duration: 5000,
      });

      onSuccess();
      onClose();
    } catch (error) {
      showToast({
        variant: 'error',
        title: 'Error',
        copy: error.message || 'Failed to add areas. Some areas may have been added. Please refresh.',
      });
      serviceLogger.error(error);
    }
  };

  const handleBack = () => {
    if (step === 'preview') {
      setStep('input');
      setPreviews([]);
    }
  };

  const totalConflicts = previews.reduce((sum, p) => sum + (p.duplicates?.length || 0), 0);
  const totalWillAdd = previews.reduce((sum, p) => sum + (p.willAdd || 0), 0);
  const facilitiesWithConflicts = previews.filter((p) => (p.duplicates?.length || 0) > 0).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-[#61b8e7]/10 rounded-lg p-2">
                  <Users className="w-5 h-5 text-[#61b8e7]" />
                </div>
                <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[22px] text-black">
                  {step === 'input' && 'Add Areas to Multiple Facilities'}
                  {step === 'preview' && 'Preview & Confirm'}
                  {step === 'processing' && 'Adding Areas...'}
                </h2>
              </div>
              {(step === 'preview' || step === 'processing') && (
                <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 ml-[52px]">
                  {step === 'preview' && 'Review changes before applying'}
                  {step === 'processing' && 'Please wait...'}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={step === 'processing'}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* STEP 1: Input */}
          {step === 'input' && (
            <div className="space-y-6">
              {/* Selected Facilities Info - shared info card style */}
              <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#61b8e7]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Info className="w-4 h-4 text-[#61b8e7]" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-[#333C4D] mb-1">
                      Bulk Mode Active
                    </p>
                    <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-[#333C4D] leading-relaxed">
                      These areas will be added to all {facilitiesCount} selected facilities:
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {isLoadingFacilities ? (
                        // Show loading state while facilities are being loaded
                        selectedFacilityIds.map((facilityId) => (
                          <span
                            key={facilityId}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-[12px] font-['Roboto:Medium',sans-serif] animate-pulse"
                          >
                            Loading...
                          </span>
                        ))
                      ) : loadedFacilities.length > 0 && loadedFacilities.every(f => f.name) ? (
                        // Show facility names when loaded
                        loadedFacilities.map((facility) => (
                          <span
                            key={facility.id}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-[12px] font-['Roboto:Medium',sans-serif]"
                          >
                            {facility.name}
                          </span>
                        ))
                      ) : facilities.length > 0 && facilities.every(f => f.name) ? (
                        // Fallback to facilities prop if loadedFacilities not ready
                        facilities.map((facility) => (
                          <span
                            key={facility.id}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-[12px] font-['Roboto:Medium',sans-serif]"
                          >
                            {facility.name}
                          </span>
                        ))
                      ) : selectedFacilityIds.length > 0 ? (
                        // Last resort: show IDs if names not available
                        selectedFacilityIds.map((facilityId) => (
                          <span
                            key={facilityId}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-[12px] font-['Roboto:Medium',sans-serif]"
                          >
                            Facility {facilityId}
                          </span>
                        ))
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              {/* Input Field */}
              <div>
                <label className="block font-['Roboto:Medium',sans-serif] text-[15px] text-gray-700 mb-2">
                  Area Names
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter area name (e.g., Kitchen, Lobby...)"
                    className="flex-1 px-4 py-2.5 border-2 border-gray-300 rounded-lg font-['Roboto:Regular',sans-serif] text-[15px] outline-none focus:border-[#61b8e7] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleAddLocationName}
                    disabled={!locationInput.trim()}
                    className="bg-[#61b8e7] text-white px-6 py-2.5 rounded-lg font-['Roboto:Medium',sans-serif] hover:bg-[#4da3d1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
              </div>

              {/* Location List */}
              {locationNames.length > 0 && (
                <div className="space-y-2">
                  <label className="block font-['Roboto:Medium',sans-serif] text-[15px] text-gray-700">
                    Areas to Add ({locationNames.length})
                  </label>
                  <div className="border-2 border-gray-200 rounded-lg p-4 space-y-2 max-h-[240px] overflow-y-auto">
                    {locationNames.map((name, index) => (
                      <div
                        key={`${name}-${index}`}
                        className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5 group hover:bg-gray-100 transition-colors"
                      >
                        <span className="font-['Roboto:Regular',sans-serif] text-[15px] text-gray-900">
                          {name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveLocationName(name)}
                          className="text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended */}
              <div className="border-t border-gray-200 pt-6">
                <label className="block font-['Roboto:Medium',sans-serif] text-[15px] text-gray-700 mb-3">
                  Recommended
                </label>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5 shrink-0">
                    <input
                      type="checkbox"
                      checked={skipDuplicates}
                      onChange={(e) => setSkipDuplicates(e.target.checked)}
                      className="size-[18px] appearance-none bg-white border-2 border-[#9e9e9e] rounded-[3px] cursor-pointer checked:bg-[#61b8e7] checked:border-[#61b8e7] transition-all group-hover:border-[#61b8e7]"
                      aria-label="Skip duplicate area names"
                    />
                    {skipDuplicates && (
                      <svg
                        className="absolute left-[3px] top-[3px] w-[12px] h-[12px] pointer-events-none"
                        fill="none"
                        viewBox="0 0 12 10"
                      >
                        <path
                          d="M1.5 5L4.5 8L10.5 1.5"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <span className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-900 group-hover:text-[#61b8e7] transition-colors">
                      Skip duplicate area names
                    </span>
                    <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 mt-0.5">
                      If a facility already has an area with the same name, skip it instead of
                      creating a duplicate
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* STEP 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-6">
              {/* Summary */}
              <div
                className={`border-2 rounded-lg p-4 ${
                  totalConflicts > 0
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-green-50 border-green-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  {totalConflicts > 0 ? (
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p
                      className={`font-['Roboto:Medium',sans-serif] text-[14px] mb-1 ${
                        totalConflicts > 0 ? 'text-amber-900' : 'text-green-900'
                      }`}
                    >
                      {totalConflicts > 0 ? 'Conflicts Detected' : 'Ready to Add'}
                    </p>
                    <p
                      className={`font-['Roboto:Regular',sans-serif] text-[13px] ${
                        totalConflicts > 0 ? 'text-amber-700' : 'text-green-700'
                      }`}
                    >
                      {totalConflicts > 0
                        ? `${totalConflicts} duplicate${
                            totalConflicts > 1 ? 's' : ''
                          } found in ${facilitiesWithConflicts} facilit${
                            facilitiesWithConflicts > 1 ? 'ies' : 'y'
                          }. ${
                            skipDuplicates
                              ? 'These will be skipped.'
                              : 'These will be created as duplicates.'
                          }`
                        : `${totalWillAdd} area${
                            totalWillAdd > 1 ? 's' : ''
                          } will be added to ${facilitiesCount} facilit${
                            facilitiesCount > 1 ? 'ies' : 'y'
                          }`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Per-Facility Preview */}
              <div className="space-y-4">
                <h3 className="font-['Roboto:SemiBold',sans-serif] text-[16px] text-gray-900">
                  Preview by Facility
                </h3>

                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {previews.map((preview) => (
                    <div
                      key={preview.facility.id}
                      className="border-2 border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      {/* Facility Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-['Roboto:SemiBold',sans-serif] text-[15px] text-gray-900">
                            {preview.facility.name}
                          </h4>
                          <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600">
                            {preview.existingLocations.length} existing area
                            {preview.existingLocations.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-2.5 py-1 bg-[#61b8e7]/10 text-[#61b8e7] rounded-md text-[13px] font-['Roboto:SemiBold',sans-serif]">
                            +{preview.willAdd} new
                          </span>
                        </div>
                      </div>

                      {/* Preview Details */}
                      <div className="space-y-2">
                        {/* New Locations */}
                        {preview.newLocations.length > 0 && (
                          <div>
                            <p className="font-['Roboto:Medium',sans-serif] text-[13px] text-green-700 mb-1 flex items-center gap-1.5">
                              <Check className="w-4 h-4 shrink-0" aria-hidden />
                              Will add ({preview.newLocations.length}):
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {preview.newLocations.map((name, idx) => (
                                <span
                                  key={`${preview.facility.id}-new-${name}-${idx}`}
                                  className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-800 rounded text-[12px] font-['Roboto:Regular',sans-serif]"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Duplicates */}
                        {preview.duplicates.length > 0 && (
                          <div>
                            <p className="font-['Roboto:Medium',sans-serif] text-[13px] text-amber-700 mb-1 flex items-center gap-1.5">
                              <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
                              {skipDuplicates ? 'Will skip' : 'Already exists'} (
                              {preview.duplicates.length}):
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {preview.duplicates.map((name, idx) => (
                                <span
                                  key={`${preview.facility.id}-dup-${name}-${idx}`}
                                  className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-[12px] font-['Roboto:Regular',sans-serif]"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No new locations */}
                        {preview.newLocations.length === 0 &&
                          preview.duplicates.length > 0 &&
                          skipDuplicates && (
                            <p className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-500 italic">
                              No new areas will be added to this facility
                            </p>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Processing */}
          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-[#61b8e7] animate-spin mb-4" />
              <h3 className="font-['Roboto:SemiBold',sans-serif] text-[18px] text-gray-900 mb-2">
                Adding Areas...
              </h3>
              <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-gray-600 mb-6">
                Please don&apos;t close this window
              </p>

              {/* Progress Bar */}
              <div className="w-full max-w-md">
                <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-[#61b8e7] h-full transition-all duration-300 ease-out"
                    style={{ width: `${processingProgress}%` }}
                  />
                </div>
                <p className="text-center font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mt-2">
                  {processingProgress}% Complete
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'processing' && (
          <div className="px-8 py-6 border-t border-gray-200 flex items-center justify-between">
            <div>
              {step === 'preview' && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="text-gray-600 hover:text-gray-900 font-['Roboto:Medium',sans-serif] text-[14px] transition-colors"
                >
                  ← Back to Edit
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:Medium',sans-serif] text-[15px] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>

              {step === 'input' && (
                <button
                  type="button"
                  onClick={handleGeneratePreview}
                  disabled={locationNames.length === 0 || isLoading}
                  className="bg-[#61b8e7] text-white rounded-lg px-6 py-2.5 font-['Roboto:Bold',sans-serif] text-[15px] hover:bg-[#4da3d1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      Preview
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}

              {step === 'preview' && (
                <button
                  type="button"
                  onClick={handleConfirmBulkAdd}
                  disabled={totalWillAdd === 0}
                  className="bg-[#61b8e7] text-white rounded-lg px-6 py-2.5 font-['Roboto:Bold',sans-serif] text-[15px] hover:bg-[#4da3d1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Add {totalWillAdd} Area{totalWillAdd !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
